<template>
  <main class="ticket-page container p-4 text-sp-primary">
    <section class="container p-0 text-sp-primary border border-white border-opacity-20 rounded-md bg-sp-secondary bg-opacity-70">

      <div v-if="checkLoading" class="fade">
        <Loading />
      </div>

      <form v-if="requestForm" @submit.prevent="submitForm">
        <div class="text-center text-xl p-4 my-2">
          {{ $t("dpo") }}
        </div>

        <div class="p-4 pt-0 gdpr-info" v-html="$t('gdprCreate')"></div>

        <div class="p-4 flex items-center 2 gap-2">
          <label class="flex items-center 2 cursor-pointer gdpr-checkbox">
            <input 
              type="checkbox" 
              v-model="isChecked" 
              class="hidden"
              @change="recordCheckboxTimestamp"
            />
            <span class="w-6 h-6 flex items-center justify-center transition-all duration-300
              border border-white border-opacity-20 rounded-md"
            >
              <svg v-if="isChecked" class="w-4 h-4 text-sp-accent" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
              </svg>
            </span>
          </label>
          {{ $t("gdprCheckbox") }}
        </div>
        
        <div class="p-4">
          <label class="block">{{ $t("gdprType") }}</label>
          <div class="issue-type flex flex-col gap-4">
            <v-select 
              v-model="gdprType" 
              :disabled="!isChecked"
              :options="[
                { label: getTranslation('gdprRestart'), value: 'dpocat_restart' },
                { label: getTranslation('gdprBored'), value: 'dpocat_bored' },
                { label: getTranslation('gpdrMultiacc'), value: 'dpocat_multiacc' },
                { label: getTranslation('gdprExpectations'), value: 'dpocat_falseadv' },
                { label: getTranslation('gdprDataSafety'), value: 'dpocat_datasafety' },
                { label: getTranslation('gdprPrices'), value: 'dpocat_prices' },
                { label: getTranslation('gdprOther'), value: 'dpocat_other' },
              ]"
              label="label" 
              :searchable="false" 
              :clearable="true"
            />
          </div>
        </div>

        <div class="flex justify-center p-4 my-2 mt-0">
          <button 
            type="submit" 
            :disabled="!isChecked || isLoading" 
            class="submit-button py-2 px-4 border-0 duration-150 rounded-md"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </form>

      <div v-if="requestConfirmed" class="p-4 gdpr-info">
        <span v-html="$t('gdprConfirmed1')"></span>
        {{ new Date().toLocaleDateString('en-US') }}. 
        <div v-html="$t('gdprConfirmed2')"></div>
      </div>

      <div v-if="cancelForm" class="p-4">
        <div class="pb-0 gdpr-info" v-html="$t('gdprCancel')"></div>
        <div class="flex justify-center my-2">
          <button type="submit" @click="cancelGdprRequest()" :disabled="isLoading" 
            class="submit-button py-2 px-4 border border-white border-opacity-20 duration-150 rounded-md"
          >
            {{ $t("gdprCancelButton") }}
          </button>
        </div>
      </div>

      <div v-if="cancelConfirmed" class="p-4 gdpr-info">
        <div v-html="$t('gdprCancelConfirmed')"></div>
      </div>
    </section>

    <Spinner v-if="isLoading" />
  </main>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useTranslate } from '../composables/useTranslate'
import vSelect from 'vue-select'
import IssueType from '../components/IssueType.vue'
import Spinner from '../components/Spinner.vue'
import Modal from '../components/Modal.vue'
import Loading from '../components/Loading.vue'
import { useRouter } from 'vue-router'
import { getUserData } from '../modules/collectData.js'
import { useLogger } from '../composables/useLogger'
import { reportError } from '../utils/reporting'

export default {
  components: {
    vSelect,
    Spinner,
    Modal,
    Loading,
    IssueType
  },
  setup() {
    const router = useRouter()
    const { t, getTranslation } = useTranslate()
    const { logClick } = useLogger()

    const gdprType = ref({ label: '', value: 'dpocat_noreason' })

    const isChecked = ref(false)
    const checkboxTs = ref(null)
    const recordCheckboxTimestamp = () => {
      if (isChecked.value) { checkboxTs.value = Date.now() }
    }

    const isLoading = ref(false)
    const checkLoading = ref(true)

    const requestForm = ref(false)
    const cancelForm = ref(false)

    const requestConfirmed = ref(false)
    const cancelConfirmed = ref(false)


    const checkGdprRequest = () => {
      const { uid, platform, game, signature } = getUserData()
      const apiUrl = import.meta.env.VITE_GDPR_URL
      const url = apiUrl + 'gdpr-wipe-request-status/hw-mb/' + 
        `${uid}` + `?uid=${uid}&platform=${platform}&game=${game}&token=${signature}`

      fetch(url, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
         }
      })
        .then(response => {
          if (!response.ok) {
            reportError(`gdpr status HTTP ${response.status}`, { source: 'GdprView.checkGdprRequest', status: response.status })
            router.push({ name: 'helpcenter' })
          }
          return response.json()
        })
        .then(data => {
          if (!data.success) {
            reportError('gdpr status: success=false', { source: 'GdprView.checkGdprRequest' })
            router.push({ name: 'helpcenter' })
            return
          }
          if (data.answer.length === 0 ) {
            requestForm.value = true
          }
          if (data.answer.length > 0 ) {
            if (data.answer[0].status === 'cancelled') {
              requestForm.value = true
            }
            else {
              cancelForm.value = true
            }
          }
          checkLoading.value = false

        })
        .catch(error => {
          reportError(error, { source: 'GdprView.checkGdprRequest' })
          router.push({ name: 'helpcenter' })
        })
    }


    const submitForm = async () => {
      isLoading.value = true

      const { uid, lang, platform, game, signature } = getUserData()
      const apiUrl = import.meta.env.VITE_GDPR_URL
      const url = apiUrl + 'gdpr-wipe-request/hw-mb' + 
        `?uid=${uid}&platform=${platform}&game=${game}&token=${signature}`

      let issueType = 'dpocat_noreason'
      if (gdprType.value === null ) { 
        issueType = 'dpocat_noreason' 
      } else if (gdprType.value != 'dpocat_noreason') { 
        issueType = gdprType.value.value
      }

      const data = {
        'locale': lang,
        "wipe_reason": issueType,
        "source": "sp",
        "account_id": uid
      }

      fetch(url, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
        .then(response => {
          if (!response.ok) {
            reportError(`gdpr submit HTTP ${response.status}`, { source: 'GdprView.submitForm', status: response.status })
            router.push({ name: 'helpcenter' })
          }
          return response.json()
        })
        .then(data => {
          if (!data.success) {
            reportError('gdpr submit: success=false', { source: 'GdprView.submitForm' })
            router.push({ name: 'helpcenter' })
            return
          }
          requestForm.value = false
          requestConfirmed.value = true
          cancelForm.value = true
          isLoading.value = false
        })
        .catch(error => {
          reportError(error, { source: 'GdprView.submitForm' })
          router.push({ name: 'helpcenter' })
        })

      sendCheckboxTs()
      logClick('gdpr.ticketSubmitted', 'gdpr.gdprForm')
    }


    const cancelGdprRequest = async () => {
      isLoading.value = true

      const { uid, platform, game, signature } = getUserData()
      const apiUrl = import.meta.env.VITE_GDPR_URL
      const url = apiUrl + 'gdpr-wipe-request-status/hw-mb/' +
        `${uid}` + `?uid=${uid}&platform=${platform}&game=${game}&token=${signature}`

      fetch(url, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(response => {
          if (!response.ok) {
            reportError(`gdpr cancel HTTP ${response.status}`, { source: 'GdprView.cancelGdprRequest', status: response.status })
            router.push({ name: 'helpcenter' })
          }
          return response.json()
        })
        .then(data => {
          if (!data.success) {
            reportError('gdpr cancel: success=false', { source: 'GdprView.cancelGdprRequest' })
            router.push({ name: 'helpcenter' })
            return
          }
          isLoading.value = false
          if (data.answer.length > 0 && data.answer[0].status === 'cancelled') {
            cancelForm.value = false
            cancelConfirmed.value = true
          } else {
            reportError('gdpr cancel: unexpected answer shape', { source: 'GdprView.cancelGdprRequest' })
            router.push({ name: 'helpcenter' })
          }
        })
        .catch(error => {
          reportError(error, { source: 'GdprView.cancelGdprRequest' })
          router.push({ name: 'helpcenter' })
        })

      logClick('gdpr.requestCancelled', 'gdpr.gdprForm')
    }


    const sendCheckboxTs = async () => {
      const { uid, iid, lang, platform, game, signature } = getUserData()
      const apiUrl = import.meta.env.VITE_API_URL
      const url = apiUrl + 'logs' +
        `?game=${game}&platform=${platform}&uid=${uid}&token=${signature}`
      
      let src = platform === '_ios' ? platform.replace('_', '').replace('s', '') : 'an'
      const data = {
        "key": "hwmb",
        "log": {
          "src": src,
          "uid": iid,
          "accountId": uid,
          "gameLanguage": lang,
          "buttonName": "gdpr.checkboxChecked",
          "windowName": "gdpr.gdprForm",
          "ts": checkboxTs.value
        }
      }

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      } catch (error) {
        return
      }
    }


    onMounted(()=> {
      checkGdprRequest()
    })

    return { 
      gdprType, checkLoading, isChecked, checkboxTs, isLoading, 
      requestForm, cancelForm, requestConfirmed, cancelConfirmed, t,
      getTranslation, recordCheckboxTimestamp, checkGdprRequest, submitForm, cancelGdprRequest, sendCheckboxTs, logClick 
    }

  }
}
</script>

<style>
.gdpr-info p {
  margin: 1rem 0;
}
.gdpr-info p:first-of-type {
  margin-top: 0;
}
.gdpr-info p:last-of-type {
  margin-bottom: 0;
}
.gdpr-info ol {
  list-style: decimal;
  padding: 0 2rem;
}
.gdpr-info a {
  text-decoration: underline;
}
.gdpr-checkbox {
  -webkit-tap-highlight-color: transparent;
}
.submit-button:disabled {
  background-color: #160D06;
  opacity: 0.4;
  cursor: not-allowed;
}
</style>